import React, { useState, useEffect } from 'react';
import styles from './styles.module.css';
import { Input, Toggle, Button } from '../../../../../../common/ui';
import ImageUpload from '../../AddNewProduct/ImageUpload';

const initialVariant = {
  variantName: '',
  code: '',
  barcode: '',
  price: '',
  cost: '',
  availability: false,
  isSerialCheck: false,
  images: [],
};

const AddUpdateVariantPopup = ({
  isOpen,
  onClose,
  newProduct,
  setNewProduct,
  selectedVariantIndex,
}) => {
  const [form, setForm] = useState(initialVariant);
  const [errors, setErrors] = useState({ variantName: '', price: '' });

  const isEditMode = selectedVariantIndex >= 0;
  const productType = newProduct?.type ?? 'STOCK';

  useEffect(() => {
    if (isOpen && isEditMode) {
      const variant = newProduct?.variantsDetails?.[selectedVariantIndex];
      if (variant) {
        setForm({
          variantName: variant.variantName ?? '',
          code: variant.variantCode ?? '',
          barcode: variant.variantBarcode ?? '',
          price:
            variant.nonStockPrice != null && variant.nonStockPrice !== ''
              ? String(variant.nonStockPrice)
              : '',
          cost:
            variant.nonStockCost != null && variant.nonStockCost !== ''
              ? String(variant.nonStockCost)
              : '',
          availability:
            variant.nonStockProductAvailability === true ||
            variant.nonStockProductAvailability === '1' ||
            variant.nonStockProductAvailability === 1,
          isSerialCheck:
            variant.variantIsSerialCheck === true ||
            variant.variantIsSerialCheck === '1' ||
            variant.variantIsSerialCheck === 1,
          images: variant.images ?? [],
        });
      }
    } else if (isOpen && !isEditMode) {
      setForm(initialVariant);
      setErrors({ variantName: '', price: '' });
    }
  }, [isOpen, isEditMode, selectedVariantIndex, newProduct?.variantsDetails]);


  const onFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const variantNameTrim = (form.variantName ?? '').trim();
    const variantNameErr = !variantNameTrim ? 'Variant name is required' : '';
    const priceErr =
      productType === 'NON_STOCK' &&
      (form.price === '' || form.price == null)
        ? 'Price is required'
        : '';
    setErrors({ variantName: variantNameErr, price: priceErr });
    return !variantNameErr && !priceErr;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const data = {
      variantName: (form.variantName ?? '').trim(),
      variantCode: form.code ?? '',
      variantBarcode: form.barcode ?? '',
      nonStockPrice:
        form.price !== '' && form.price != null ? parseFloat(form.price) : 0,
      nonStockCost:
        form.cost !== '' && form.cost != null ? parseFloat(form.cost) : 0,
      nonStockProductAvailability: !!form.availability,
      variantIsSerialCheck: !!form.isSerialCheck,
      images: form.images,
    };

    if (isEditMode) {
      const existing = newProduct.variantsDetails[selectedVariantIndex] || {};
      setNewProduct((prev) => ({
        ...prev,
        variantsDetails: prev.variantsDetails.map((v, i) =>
          i === selectedVariantIndex ? { ...existing, ...data } : v
        ),
      }));
      onClose?.();
    } else {
      setNewProduct((prev) => ({
        ...prev,
        variantsDetails: [...(prev.variantsDetails ?? []), data],
      }));
      setForm(initialVariant);
      setErrors({ variantName: '', price: '' });
    }
  };

  const handleReset = () => {
    setForm(initialVariant);
    setErrors({ variantName: '', price: '' });
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-update-variant-title"
    >
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.headerBar}>
          <h2 id="add-update-variant-title" className={styles.headerTitle}>
            {isEditMode ? 'Edit Product Variant' : 'Add New Product Variant'}
          </h2>
        </div>
        <div className={styles.content}>
          <div className={styles.formRow}>
            <div className={styles.fieldGroup}>
              <Input
                label="Variant Name"
                type="text"
                value={form.variantName ?? ''}
                onChange={(value) => onFieldChange('variantName', value)}
                placeholder="Enter variant name"
                error={errors.variantName}
              />
            </div>
            <div className={styles.fieldGroup}>
              <Input
                label="Code"
                type="text"
                value={form.code ?? ''}
                onChange={(value) => onFieldChange('code', value)}
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.fieldGroup}>
              <Input
                label="Barcode"
                type="text"
                value={form.barcode ?? ''}
                onChange={(value) => onFieldChange('barcode', value)}
              />
            </div>
            <div className={styles.fieldGroup} />
          </div>
          {productType === 'NON_STOCK' && (
            <>
              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Price"
                    type="text"
                    value={form.price ?? ''}
                    onChange={(value) => onFieldChange('price', value)}
                    placeholder="0.00"
                    error={errors.price}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Input
                    label="Cost"
                    type="text"
                    value={form.cost ?? ''}
                    onChange={(value) => onFieldChange('cost', value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Toggle
                    label="Availability"
                    labelPlacement="start"
                    name="variantAvailability"
                    checked={form.availability}
                    onChange={(e) =>
                      onFieldChange('availability', e.target.checked)
                    }
                  />
                </div>
                <div className={styles.fieldGroup} />
              </div>
            </>
          )}
          {productType === 'STOCK' && (
            <div className={styles.formRow}>
              <div className={styles.fieldGroup}>
                <Toggle
                  label="Serial Number Validation"
                  labelPlacement="start"
                  name="variantSerialCheck"
                  checked={form.isSerialCheck}
                  onChange={(e) =>
                    onFieldChange('isSerialCheck', e.target.checked)
                  }
                />
              </div>
              <div className={styles.fieldGroup} />
            </div>
          )}

          <div className={styles.imageDivider} />

          <ImageUpload
            images={form.images}
            onChange={(imgs) => onFieldChange('images', imgs)}
          />
        </div>
        <div className={styles.footerBar}>
          <Button type="button" onClick={handleSubmit}>
            {isEditMode ? 'Update' : 'Add variant'}
          </Button>
          {!isEditMode && (
            <Button type="button" variant="secondary" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddUpdateVariantPopup;
